import { AxiosError } from 'axios'

/**
 * Извлекает сообщение об ошибке из axios ошибки.
 * Приоритет: response.data.message > response.data.error > error.message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const axiosError = error as AxiosError<{ message?: string; error?: string }>
    
    // Если это axios ошибка с response
    if (axiosError.response?.data) {
      const data = axiosError.response.data
      
      // Приоритет: message из ответа сервера
      if (data.message) {
        return data.message
      }
      
      // Если есть error поле
      if (data.error) {
        return data.error
      }
    }
    
    // Fallback на стандартное сообщение ошибки
    return error.message
  }
  
  // Если это не Error объект
  return String(error)
}

/** Технические axios/сетевые тексты — не показывать пользователю как есть. */
export function isTechnicalErrorMessage(message: string): boolean {
  return (
    /^Network Error$/i.test(message) ||
    /^Request failed with status code \d+$/i.test(message) ||
    /^timeout of \d+ms exceeded$/i.test(message)
  )
}

/** Сообщение для Alert: серверное оставляем, техническое заменяем на i18n fallback. */
export function displayErrorMessage(message: string | null | undefined, fallback: string): string {
  if (!message || isTechnicalErrorMessage(message)) {
    return fallback
  }
  return message
}

/**
 * Ошибки полей из ответа 422 Laravel (`errors`): имя поля → сообщения.
 *
 * Ключи элементов списка приходят с индексом: `student_ids.2`. null — ответ
 * без ошибок полей (другой статус, сеть, 422 из исключения с одним `message`).
 */
export function getValidationErrors(error: unknown): Record<string, string[]> | null {
  if (typeof error !== 'object' || error === null) {
    return null
  }

  const e = error as {
    response?: { status?: number; data?: { errors?: unknown } }
  }
  const errors = e.response?.data?.errors

  if (e.response?.status !== 422 || typeof errors !== 'object' || errors === null || Array.isArray(errors)) {
    return null
  }

  const result: Record<string, string[]> = {}
  for (const [field, messages] of Object.entries(errors as Record<string, unknown>)) {
    const list = (Array.isArray(messages) ? messages : [messages]).filter(
      (message): message is string => typeof message === 'string' && message !== '',
    )
    if (list.length > 0) {
      result[field] = list
    }
  }

  return Object.keys(result).length > 0 ? result : null
}

/** Отклонённые элементы списка в ответе 422 */
export interface ListItemValidationErrors {
  /** Индексы отклонённых элементов в отправленном списке, по возрастанию */
  indexes: number[]
  /** Первое сообщение сервера об отклонённом элементе */
  message: string
}

/**
 * Какие элементы списка `field` отклонил сервер: по ключам `field.N`.
 * null — ни один элемент списка не отклонён.
 */
export function getListItemValidationErrors(
  errors: Record<string, string[]> | null,
  field: string,
): ListItemValidationErrors | null {
  if (!errors) {
    return null
  }

  const prefix = `${field}.`
  const indexes: number[] = []
  let message = ''

  for (const [key, messages] of Object.entries(errors)) {
    if (!key.startsWith(prefix)) {
      continue
    }
    const index = Number(key.slice(prefix.length))
    if (!Number.isInteger(index) || index < 0) {
      continue
    }
    indexes.push(index)
    message = message || messages[0]
  }

  if (indexes.length === 0) {
    return null
  }

  return { indexes: indexes.sort((a, b) => a - b), message }
}

/** Payload 422 storage_limit_exceeded (лимит дискового пространства аудио) */
export interface StorageLimitErrorPayload {
  /** Локализованное сообщение сервера */
  message: string | null
  usedBytes: number | null
  limitBytes: number | null
}

/**
 * Разбор ошибки лимита аудио-хранилища. Такой 422 отдают генерация озвучки,
 * загрузка аудио слова и импорт словаря; null — ошибка другого типа.
 */
export function parseStorageLimitError(error: unknown): StorageLimitErrorPayload | null {
  if (typeof error !== 'object' || error === null) {
    return null
  }

  const e = error as {
    response?: {
      status?: number
      data?: {
        error?: string
        message?: string
        data?: { used_bytes?: number; limit_bytes?: number | null }
      }
    }
  }

  if (e.response?.status !== 422 || e.response.data?.error !== 'storage_limit_exceeded') {
    return null
  }

  return {
    message: e.response.data.message ?? null,
    usedBytes: e.response.data.data?.used_bytes ?? null,
    limitBytes: e.response.data.data?.limit_bytes ?? null,
  }
}
